import { Download, FileWarning, ShieldCheck, Upload } from "lucide-react";

import { CountdownChip } from "../components/CountdownChip";
import { useCoiOnFile } from "../api";
import { formatDate, formatMoneyCents } from "../format";

// Phase 4a customer-portal FR-9 (COI on file tab). Read-only — the
// upload flow lives in the existing 2b CoiPage at /account/coi.
//
// Phase 4u portal-profile-polish — design grade
// `.notes/design-grades/portal/03-profile.md` asked for a renewal
// countdown chip, a "Replace COI" action that doesn't require finding
// the legacy /account/coi page on your own, and a card treatment that
// matches the rest of the polished tabs.

const DAY_MS = 86_400_000;
// Beyond ~120 days the chip becomes noise — the audit's "11 months out"
// example is real but the customer can't realistically renew a policy
// that far ahead. Inside the threshold we want the chip; outside, the
// plain "Expires …" date carries enough information.
const CHIP_VISIBLE_WITHIN_DAYS = 120;

function daysUntil(iso: string | null | undefined): number | null {
  if (iso == null) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d).getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today.getTime()) / DAY_MS);
}

export function CoiTab(): JSX.Element {
  const q = useCoiOnFile();
  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-brand-red">Couldn&rsquo;t load COI.</div>;

  const coi = q.data.current;
  if (coi == null) {
    return (
      <div
        className="rounded-lg border border-amber-300 bg-amber-50 p-4"
        data-testid="coi-missing"
      >
        <div className="flex items-start gap-3">
          <FileWarning aria-hidden="true" className="mt-0.5 h-5 w-5 text-amber-700" />
          <div className="flex-1 space-y-2">
            <p className="text-sm font-semibold text-amber-900">
              No certificate of insurance on file
            </p>
            <p className="text-sm text-amber-900/90">
              Upload an ACORD 25 before your next rental so we can release equipment without delay.
            </p>
            <a
              href="/account/coi"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm transition hover:bg-brand-red-dark focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2"
              data-testid="coi-upload-cta"
            >
              <Upload aria-hidden="true" className="h-4 w-4" />
              Upload COI
            </a>
          </div>
        </div>
      </div>
    );
  }

  const days = daysUntil(coi.expiry_date);
  const showChip = days != null && days <= CHIP_VISIBLE_WITHIN_DAYS;

  return (
    <div className="space-y-3">
      {coi.expired ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-brand-red/40 bg-brand-red/5 p-3 text-sm text-brand-red"
          data-testid="coi-expired"
        >
          <FileWarning aria-hidden="true" className="mt-0.5 h-4 w-4" />
          <span>
            <strong>Expired.</strong> Upload a renewal before your next rental.
          </span>
        </div>
      ) : null}

      <article
        className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
        data-testid="coi-card"
      >
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck aria-hidden="true" className="h-5 w-5 text-flag-navy" />
            <h2 className="text-base font-semibold text-brand-black">
              {coi.insurer ?? "Insurer on file"}
            </h2>
          </div>
          {showChip ? <CountdownChip label="Expires" days={days} /> : null}
        </header>

        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
          {coi.policy_number ? (
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Policy #
              </dt>
              <dd className="font-mono text-brand-black">{coi.policy_number}</dd>
            </div>
          ) : null}
          {coi.gl_limit_cents != null ? (
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                GL limit
              </dt>
              <dd className="text-brand-black">{formatMoneyCents(coi.gl_limit_cents)}</dd>
            </div>
          ) : null}
          {coi.expiry_date ? (
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Expires
              </dt>
              <dd className="text-brand-black">{formatDate(coi.expiry_date)}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Status
            </dt>
            <dd className="text-brand-black">{coi.status}</dd>
          </div>
        </dl>

        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-zinc-100 pt-3">
          <a
            href="/account/coi"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm transition hover:bg-brand-red-dark focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2"
            data-testid="coi-replace-cta"
          >
            <Upload aria-hidden="true" className="h-4 w-4" />
            Replace COI
          </a>
          <a
            href={coi.pdf_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-flag-navy transition hover:text-brand-red hover:underline"
            data-testid="coi-pdf-link"
          >
            <Download aria-hidden="true" className="h-4 w-4" />
            Download current PDF
          </a>
        </div>
      </article>
    </div>
  );
}
